import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatApiError } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, GearSix, Tag, Target, X, Check, Funnel, File, UsersFour, Buildings } from '@phosphor-icons/react';
import api from '../../lib/api';

const SETTING_TYPES = [
  { key: 'lead_sources', label: 'Lead Sources', icon: Funnel, description: 'Where your leads come from' },
  { key: 'campaign_names', label: 'Campaign Names', icon: Target, description: 'Marketing campaign names' },
  { key: 'close_reasons', label: 'Close Reasons', icon: X, description: 'Why leads are marked as closed' },
  { key: 'follow_up_types', label: 'Follow-up Types', icon: Tag, description: 'Types of client follow-ups' },
  { key: 'task_types', label: 'Task Types', icon: Tag, description: 'Types of operational tasks (NFT / FT / SIP)' },
  { key: 'amc_names', label: 'AMC Names', icon: Buildings, description: 'Asset Management Company names (used in tasks and folios)' },
  { key: 'document_types', label: 'Document Types', icon: File, description: 'Types of KYC and client documents' },
  { key: 'client_types', label: 'Client Types', icon: UsersFour, description: 'Entity types for clients (Individual, HUF, Firm, etc.)' },
];

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('lead_sources');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);

  const fetchSettings = async (type) => {
    try {
      const res = await api.get(`/settings/${type}`);
      setSettings(prev => ({ ...prev, [type]: res.data.items }));
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      for (const type of SETTING_TYPES) {
        await fetchSettings(type.key);
      }
      setLoading(false);
    };
    loadAll();
  }, []);

  const handleToggle = async (type, index, isActive) => {
    const updated = [...settings[type]];
    updated[index].is_active = isActive;
    setSettings(prev => ({ ...prev, [type]: updated }));

    try {
      await api.put(`/settings/${type}`, { items: updated });
      toast.success('Setting updated');
    } catch (error) {
      toast.error(formatApiError(error));
      // Revert
      updated[index].is_active = !isActive;
      setSettings(prev => ({ ...prev, [type]: updated }));
    }
  };

  const handleAddItem = async () => {
    if (!newItem.trim()) return;
    setSaving(true);
    try {
      await api.post(`/settings/${activeTab}/add`, { value: newItem.trim(), is_active: true });
      toast.success('Item added successfully');
      setNewItem('');
      setShowAddDialog(false);
      await fetchSettings(activeTab);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (type, index) => {
    const updated = settings[type].filter((_, i) => i !== index);
    setSettings(prev => ({ ...prev, [type]: updated }));

    try {
      await api.put(`/settings/${type}`, { items: updated });
      toast.success('Item removed');
    } catch (error) {
      toast.error(formatApiError(error));
      await fetchSettings(type);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <GearSix size={48} className="mx-auto text-zinc-300 mb-4" />
        <h2 className="text-xl font-semibold">Access Restricted</h2>
        <p className="text-zinc-500 mt-2">Only administrators can manage settings.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Settings
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Manage dropdown options for leads, campaigns, and more
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-100 p-1 rounded-sm flex-wrap h-auto gap-1">
          {SETTING_TYPES.map(type => (
            <TabsTrigger 
              key={type.key} 
              value={type.key} 
              className="rounded-sm text-xs uppercase tracking-wider"
            >
              <type.icon size={14} className="mr-1" />
              {type.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {SETTING_TYPES.map(type => (
          <TabsContent key={type.key} value={type.key} className="mt-6">
            <Card className="border-zinc-200 rounded-sm">
              <CardHeader className="border-b border-zinc-100 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <type.icon size={18} className="text-[#002FA7]" />
                    {type.label}
                  </CardTitle>
                  <CardDescription className="mt-1">{type.description}</CardDescription>
                </div>
                <Dialog open={showAddDialog && activeTab === type.key} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-[#002FA7] text-white rounded-sm" data-testid={`add-${type.key}-button`}>
                      <Plus size={16} className="mr-1" /> Add New
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New {type.label.slice(0, -1)}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase">Name</Label>
                        <Input
                          value={newItem}
                          onChange={(e) => setNewItem(e.target.value)}
                          placeholder={`Enter ${type.label.slice(0, -1).toLowerCase()} name`}
                          className="rounded-sm"
                          data-testid="new-setting-input"
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => { setShowAddDialog(false); setNewItem(''); }}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleAddItem} 
                          disabled={saving || !newItem.trim()}
                          className="bg-[#002FA7] text-white"
                          data-testid="save-setting-button"
                        >
                          {saving ? 'Adding...' : 'Add'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10" />)}
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-100">
                    {settings[type.key]?.map((item, index) => (
                      <div 
                        key={index} 
                        className="p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                        data-testid={`setting-item-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={item.is_active ? 'badge-active' : 'badge-inactive'}>
                            {item.is_active ? <Check size={12} /> : <X size={12} />}
                          </Badge>
                          <span className={`text-sm ${!item.is_active ? 'text-zinc-400 line-through' : ''}`}>
                            {item.value}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-zinc-500">Active</Label>
                            <Switch
                              checked={item.is_active}
                              onCheckedChange={(checked) => handleToggle(type.key, index, checked)}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(type.key, index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(!settings[type.key] || settings[type.key].length === 0) && (
                      <div className="p-6 text-center text-zinc-500">
                        No items configured. Add your first one!
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
